import type { ApolloQueryResult } from "@apollo/client";
import { useQuery } from "@apollo/client";
import { useMemo } from "react";

import {
  GET_GIT_ROOTS_QUERIES,
  GET_GROUP_ENV_URLS_FRAGMENT,
  GET_ORGANIZATION_EXTERNAL_ID,
} from "../../queries";
import type { IEnvironmentUrlAttr } from "../../types";
import { getEnvsFromResult } from "../utils";
import { getFragmentData } from "gql/fragment-masking";
import type { GetGitRootsViewQueriesQuery } from "gql/graphql";
import { useAuthz } from "hooks";
import { Logger } from "utils/logger";

interface IUseGitRootQueries {
  externalId?: string;
  gitEnvironmentUrls: IEnvironmentUrlAttr[];
  loading: boolean;
  refetch: () => Promise<ApolloQueryResult<GetGitRootsViewQueriesQuery>>;
}

const useGitRootQueries = (
  groupName: string,
  organizationName: string,
): IUseGitRootQueries => {
  const { canResolve } = useAuthz();

  const { data, refetch, loading } = useQuery(GET_GIT_ROOTS_QUERIES, {
    onError: ({ graphQLErrors }): void => {
      graphQLErrors.forEach((error): void => {
        Logger.error("Couldn't load Git roots queries", error);
      });
    },
    variables: {
      canGetSecrets: canResolve("git_environment_url_secrets"),
      groupName,
      organizationName,
    },
  });

  const envUrlsResult = getFragmentData(GET_GROUP_ENV_URLS_FRAGMENT, data);
  const externalIdResult = getFragmentData(GET_ORGANIZATION_EXTERNAL_ID, data);

  const externalId = externalIdResult?.organizationId.awsExternalId;
  const gitEnvironmentUrls = useMemo(
    (): IEnvironmentUrlAttr[] => getEnvsFromResult(envUrlsResult),
    [envUrlsResult],
  );

  return { externalId, gitEnvironmentUrls, loading, refetch };
};

export { useGitRootQueries };
